/**
 * materializeRoyaltyPayouts.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Persist the royalty payment to the mineral owner of a closed deal
 * as a single row in `royalty_payouts`. Drives 1099-MISC Box 2
 * eligibility (≥ $10 / year aggregate per recipient).
 *
 * The "royalty" on a deal-close from the firm's perspective is what
 * the firm paid the mineral owner to acquire their interest — i.e.
 * the deal's buyTotal. Each closed deal produces exactly one
 * royalty_payouts row keyed to the owner's contact, idempotent on
 * (deal_id, contact_id).
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { rollupEstimator, estimatorHasData } from './interestEstimator';
import { normalizeStage } from '../data/dealStages';

const CLOSED_STAGES = new Set(['Closed', 'Funded']);

export function buildRoyaltyPayoutRow(deal) {
  if (!deal?.id || !deal.contactId) return null;
  if (!CLOSED_STAGES.has(normalizeStage(deal.stage))) return null;
  const rows = Array.isArray(deal.interestEstimates) ? deal.interestEstimates : [];
  if (!estimatorHasData(rows)) return null;
  const er = rollupEstimator(rows);
  const buyTotal = er.mi.buyTotal + er.wi.buyTotal;
  if (buyTotal <= 0) return null;
  const paidOn = deal.sellClosingDate || deal.buyClosingDate || deal.closingDate || null;
  return {
    dealId: deal.id,
    contactId: deal.contactId,
    payoutAmount: buyTotal,
    paidOn,
  };
}

export async function materializeRoyaltyPayouts(deal, store) {
  const row = buildRoyaltyPayoutRow(deal);
  if (!row) return [];
  return store.insertRoyaltyPayouts([row]);
}
